/**
 * Engagement API Client Interface
 * Pure API contract - implementation in hooks/use-engagement-api.ts
 * 
 * Defines methods for interacting with the versioned engagement API
 */

import { 
  Engagement, 
  EngagementSearchResponse, 
  EngagementResponse, 
  EngagementCollectionResponse,
  TransactionHeaderSummary,
  LookupCategory,
  LookupValue,
  Operation,
  EngagementFieldMetadata,
  CompleteEngagementRequest
} from './engagement'

export interface EngagementApiClient {
  // Engagement Search Operations (Public Search API)
  
  /**
   * Searches and lists engagements with filtering and field selection
   */
  searchEngagements(
    search?: string,        // $search parameter
    filter?: string,        // $filter parameter (OData format)
    orderby?: string,       // $orderby parameter (OData format)
    top?: number,           // $top parameter (pagination)
    skip?: number,          // $skip parameter (pagination)
    select?: string        // $select parameter (field selection)
  ): Promise<EngagementSearchResponse>

  // Core Engagement Operations (Internal Management - Version Aware)
  
  /**
   * Gets all versions of an engagement by business ID
   */
  getEngagementVersions(recordId: number): Promise<EngagementCollectionResponse>
  
  /**
   * Gets a specific version of an engagement
   */
  getEngagementVersion(recordId: number, version: number): Promise<EngagementResponse>
  
  /**
   * Creates a new engagement with default values
   */
  createEngagementWithDefaults(): Promise<EngagementResponse>
  
  /**
   * Creates a new engagement with provided data
   */
  createEngagementWithData(engagement: Partial<Omit<Engagement, 'recordId' | 'version' | 'id' | 'createdDateTime' | 'lastModifiedDateTime' | 'createdByProfileId' | 'lastModifiedByProfileId'>>): Promise<EngagementResponse>
  
  /**
   * Updates an engagement using JSON Patch operations
   */
  updateEngagement(recordId: number, version: number, operations: Operation[]): Promise<EngagementResponse>
  
  /**
   * Saves changes to an engagement using JSON Patch operations (state-aware)
   */
  saveEngagement(recordId: number, version: number, operations: Operation[]): Promise<EngagementResponse>
  
  /**
   * Deletes a specific version of an engagement
   */
  deleteEngagement(recordId: number, version: number): Promise<void>
  
  // Change Request Operations
  
  /**
   * Creates a new version (change request) of an existing engagement
   */
  createChangeRequest(recordId: number, comment: string): Promise<EngagementResponse>
  
  // Workflow Operations
  
  /**
   * Submits a draft engagement for review
   */
  submitEngagement(recordId: number, version: number): Promise<EngagementResponse>
  
  /**
   * Submits an accounting draft engagement for review
   */
  submitForReview(recordId: number, version: number): Promise<EngagementResponse>
  
  /**
   * Approves a pending engagement
   */
  approveEngagement(recordId: number, version: number): Promise<EngagementResponse>
  
  /**
   * Declines a pending engagement
   */
  declineEngagement(recordId: number, version: number): Promise<EngagementResponse>
  
  /**
   * Discards a draft engagement
   */
  discardEngagement(recordId: number, version: number): Promise<EngagementResponse>
  
  /**
   * Generates a milestone transaction for a specific milestone
   */
  generateMilestoneTransaction(recordId: number, version: number, milestoneReference: string): Promise<TransactionHeaderSummary>
  
  /**
   * Gets existing milestone transactions for a specific milestone
   */
  getMilestoneTransactions(recordId: number, version: number, milestoneReference: string): Promise<TransactionHeaderSummary[]>
  
  /**
   * Recalls an engagement back to draft status
   */
  recallEngagement(recordId: number, version: number): Promise<EngagementResponse>
  
  /**
   * Recalls an engagement back to accounting draft status
   */
  recallToAccounting(recordId: number, version: number): Promise<EngagementResponse>
  
  /**
   * Marks an active engagement as completed
   */
  completeEngagement(recordId: number, version: number, request: CompleteEngagementRequest): Promise<EngagementResponse>
  
  /**
   * Reactivates a completed engagement
   */
  reactivateEngagement(recordId: number, version: number): Promise<EngagementResponse>
  
  // Lookup Data Operations
  
  /**
   * Gets all available lookup categories and their values
   */
  getAllLookups(): Promise<LookupCategory[]>
  
  /**
   * Gets lookup values for a specific category
   */
  getLookupsByCategory(categoryName: string): Promise<LookupValue[]>
  
  // External Domain Operations (for reference)
  
  /**
   * Gets the active version of an engagement by business ID
   */
  getActiveEngagement(recordId: number): Promise<EngagementResponse>

  // Metadata Operations

  /**
   * Gets field metadata for the Engagement model
   */
  getFieldMetadata(): Promise<EngagementFieldMetadata>
}

// API Configuration
export interface ApiConfig {
  baseUrl: string
  token?: string
  timeout?: number
  headers?: Record<string, string>
}

// Error Types
export class EngagementApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: any
  ) {
    super(message)
    this.name = 'EngagementApiError'
  }
}

// JSON Patch helper types
export type PatchOperation = 
  | { op: 'replace'; path: string; value: any }
  | { op: 'add'; path: string; value: any }
  | { op: 'remove'; path: string }
  | { op: 'move'; path: string; from: string }
  | { op: 'copy'; path: string; from: string }
  | { op: 'test'; path: string; value: any }

// Common API response wrapper
export interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  status: number
} 